import { Avatar, type SxProps, type Theme } from '@mui/material';

const PRESENCE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function isOnline(lastActiveAt?: string): boolean {
  if (!lastActiveAt) return false;
  const ts = new Date(lastActiveAt).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < PRESENCE_TIMEOUT_MS;
}

/**
 * Computes up to 2-character initials from a display name.
 */
export const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

export interface UserAvatarProps {
  /** Display name used for initials fallback and alt text. */
  name: string;
  /** Optional photo URL. When provided, renders an image instead of initials. */
  photoUrl?: string | null;
  /** Avatar diameter in pixels. Defaults to 40. */
  size?: number;
  /** When true, renders a lime-green presence ring around the avatar. */
  online?: boolean;
  /** ISO timestamp of user's last activity. Used to auto-derive online status. */
  lastActiveAt?: string;
  /** Whether to show presence indicator. Defaults to true. */
  showPresence?: boolean;
  /** MUI sx overrides applied to the root Avatar. */
  sx?: SxProps<Theme>;
}

/**
 * Reusable avatar component that renders a user's photo when available
 * and falls back to their initials on a dark background.
 *
 * Presence can be set explicitly via `online` or derived from `lastActiveAt`.
 */
export const UserAvatar = ({
  name,
  photoUrl,
  size = 40,
  online,
  lastActiveAt,
  showPresence = true,
  sx,
}: UserAvatarProps) => {
  const fontSize = Math.max(10, Math.round(size * 0.3));
  const isActive = online ?? (showPresence && isOnline(lastActiveAt));

  const dotSize = Math.max(8, Math.round(size * 0.25));

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <Avatar
        data-testid="user-avatar"
        data-online={isActive ? 'true' : 'false'}
        src={photoUrl ?? undefined}
        alt={name}
        sx={{
          width: size,
          height: size,
          fontSize,
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 600,
          bgcolor: 'rgb(68, 68, 68)',
          color: 'var(--tx-text)',
          ...((sx ?? {}) as Record<string, unknown>),
        }}>
        {getInitials(name)}
      </Avatar>
      {isActive && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: 'var(--tx-lime)',
            border: '2px solid var(--tx-line)',
          }}
        />
      )}
    </div>
  );
};
